/**
 * @file Builds the radio input that lets the investor pick an existing
 * profile of the given type or start a new one.
 */
import keyBy from "lodash.keyby";
import type { RadioChangeEvent } from "antd";

import { TYPE_RADIO, TYPE_HIDDEN } from "@components/Form";
import {
  INVESTMENT_PROFILE_FIELD_MAP,
  INVESTMENT_PROFILE_TYPE_JOINT,
  INVESTMENT_PROFILE_TYPE_SDIRA,
  INVESTMENT_PROFILE_TYPE_ENTITY,
} from "@components/Domain";

import type { Form, Input, Profile, ProfileField, ProfileFields, UpdateSchema } from "../types";

const LABEL_PROFILE = "Profile";
const LABEL_NEW_PROFILE = "New Profile";
const LABEL_PLACEHOLDER = "Select existing or create a new one";

const NA_PROFILE = "NA";
const NEW_PROFILE = "new_profile";

const PREFIX_MAP: Record<string, string> = {
  [INVESTMENT_PROFILE_TYPE_SDIRA]: "SDIRA",
  [INVESTMENT_PROFILE_TYPE_JOINT]: "Joint",
  [INVESTMENT_PROFILE_TYPE_ENTITY]: "Entity",
};

const HIDDEN_PROFILE_NAME_INPUT: Input = {
  name: "profileName",
  type: TYPE_HIDDEN,
  initialValue: NEW_PROFILE,
};

export type ProfileExtra = (selectedProfile?: ProfileFields) => Record<string, unknown>;

/**
 * Builds the profile-selection input for the given profile type, falling
 * back to a hidden new-profile input when there is no usable profile to
 * pick from.
 */
const computeProfileInput = (
  profileType: string,
  updateSchema: UpdateSchema,
  profiles: Profile[],
  profileExtra: ProfileExtra = () => ({}),
): Input[] => {
  const hasProfiles = profiles.length > 0;

  if (!hasProfiles) {
    return [ { ...HIDDEN_PROFILE_NAME_INPUT } ];
  }

  const profileOptions = profiles
    .filter(({ disabled }) => !disabled)
    .filter(({ name }) => name !== NA_PROFILE && !name.endsWith(`& ${NA_PROFILE}`))
    .map(({ name: label }) => ({ value: label, label }));

  const hasProfileOptions = profileOptions.length > 0;

  if (!hasProfileOptions) {
    return [ { ...HIDDEN_PROFILE_NAME_INPUT } ];
  }

  const profileField = INVESTMENT_PROFILE_FIELD_MAP[profileType] as ProfileField;
  const prefix = PREFIX_MAP[profileType];

  const options = [
    ...profileOptions,
    {
      value: NEW_PROFILE,
      label: LABEL_NEW_PROFILE,
    },
  ];

  const profileMapByName: Record<string, Profile> = keyBy(profiles, "name");

  /**
   * Fills the profile field from the chosen existing profile, or resets it
   * to a new profile, then refreshes the schema.
   */
  const onChange = ({ target }: RadioChangeEvent, form: Form) => {
    const { value: name } = target;

    const isProfileChosen = name !== NEW_PROFILE;

    if (isProfileChosen) {
      const selectedProfile = profileMapByName[name][profileField];
      const extraProfileAttributes = profileExtra(selectedProfile);
      const profile = {
        ...selectedProfile,
        ...extraProfileAttributes,
        profileName: name,
      };

      const profileValue = { [profileField]: profile };

      form.setFieldsValue(profileValue);
      updateSchema();
      return;
    }

    form.resetFields([ profileField ]);
    form.setFieldsValue({ [profileField]: { profileName: NEW_PROFILE } });

    updateSchema();
  };

  return [{
    name: "profileName",
    type: TYPE_RADIO,
    label: `${prefix} ${LABEL_PROFILE}`,
    required: true,
    placeholder: LABEL_PLACEHOLDER,
    inputProps: {
      onChange,
    },
    options,
  }];
};

export default computeProfileInput;
